import { isEmpty } from 'lodash';
import logError from 'App/babel/helpers/logError';
import type { Order } from 'App/resources/Model/Order';
import { getObjectFromTranslatedMapping, translateTagsInObject } from '../UiWidgetHelpers';

type FieldActions = {
	updated?: {
		order: Partial<Order>;
	};
	disabled?: {
		order: { [key: string]: boolean | FieldActions };
	};
	visible?: {
		order: { [key: string]: boolean | FieldActions };
	};
};

type HandleResponseParams = {
	responses: any[];
	responseHandling: string;
	responseMapping?: string;
	dataSources: any;
	widgetExternalId: string;
	widgetSettings: any;
	user: any;
	updateOrderFromApp?: (fieldActions: FieldActions, order?: Order) => void;
	setResponseData?: (data: any) => void;
	errorContext?: string;
};

export const handleWidgetResponse = ({
	responses,
	responseHandling,
	responseMapping,
	dataSources,
	widgetExternalId,
	widgetSettings,
	user,
	updateOrderFromApp,
	setResponseData,
	errorContext = 'widget'
}: HandleResponseParams) => {
	if (responseHandling === 'none' || !responses.length) return;

	const successfulResponses = responses.filter(r => {
		if (!r || typeof r !== 'object' || isEmpty(r)) return false;
		return !Object.values(r).some(val => val && typeof val === 'object' && 'error' in val);
	});

	if (!successfulResponses.length) {
		return;
	}

	try {
		if (responseHandling === 'updateOrder') {
			if (!updateOrderFromApp) return;

			if (!responseMapping) {
				logError(
					new Error('No response mapping configured'),
					'Response mapping is required for updateOrder handling'
				);
				return;
			}

			try {
				if (!responseMapping.trim()) {
					logError(new Error('Empty response mapping'), 'Response mapping cannot be empty');
					return;
				}
				const fieldActions: FieldActions = {
					updated: { order: {} },
					disabled: { order: {} },
					visible: { order: {} }
				};

				for (const response of successfulResponses) {
					const combinedData = {
						...dataSources,
						...(response as any),
						providerData: user?.integrations?.[widgetExternalId]?.providerData || {}
					};
					const translatedMapping = translateTagsInObject(responseMapping, combinedData, widgetSettings);

					let translatedMappingAsObject = translatedMapping;
					if (typeof translatedMapping === 'string') {
						translatedMappingAsObject = getObjectFromTranslatedMapping(translatedMapping);
					}

					if (response.orderRow && typeof translatedMappingAsObject !== 'string') {
						if (!fieldActions.updated!.order!.orderRow) {
							fieldActions.updated!.order!.orderRow = [];
						}

						const updateAction = {
							...translatedMappingAsObject,
							sortId: response.orderRow.sortId
						};
						if (updateAction.id) {
							updateAction.id = response.orderRow.id;
						}

						fieldActions.updated!.order.orderRow.push(updateAction);
					} else {
						fieldActions.updated!.order = {
							...fieldActions.updated!.order,
							...translatedMappingAsObject
						};
					}
				}

				updateOrderFromApp(fieldActions);
			} catch (parseError) {
				logError(parseError, 'Failed to process response mapping configuration');
			}
		} else if (responseHandling === 'showRows') {
			if (setResponseData) {
				setResponseData(successfulResponses);
			}
		}
	} catch (error) {
		logError(error, `Failed to handle ${errorContext} response`);
	}
};

type ProcessOrderRowsParams = {
	orderRows: any[];
	dataSourceName: string;
	dataSourceSettings: Record<string, any>;
	dataSources: any;
	widgetExternalId: string;
	user: any;
	getDataSource: (widgetExternalId: string, config: any) => Promise<any>;
	batchSize?: number;
};

export const processOrderRowsInBatches = async ({
	orderRows,
	dataSourceName,
	dataSourceSettings,
	dataSources,
	widgetExternalId,
	user,
	getDataSource,
	batchSize = 5
}: ProcessOrderRowsParams): Promise<any[]> => {
	const results: any[] = [];
	if (!dataSourceSettings) {
		return results;
	}

	for (let i = 0; i < orderRows.length; i += batchSize) {
		const batch = orderRows.slice(i, i + batchSize);
		const batchPromises = batch.map(async (orderRow, index) => {
			try {
				const translatedDataSourceConfig = translateTagsInObject(
					dataSourceSettings[dataSourceName],
					{
						...dataSources,
						providerData: user?.integrations?.[widgetExternalId]?.providerData || {},
						orderRow
					},
					true
				);
				if (!translatedDataSourceConfig) {
					return Promise.resolve(null);
				}
				const response = await getDataSource(widgetExternalId, {
					[dataSourceName]: translatedDataSourceConfig
				});
				response.orderRow = orderRow;
				return response;
			} catch (error) {
				logError(error, `Failed to process order row ${i + index + 1}`);
				return { error: (error as Error).message || 'Failed to process order row' };
			}
		});

		const batchResults = await Promise.all(batchPromises);
		const batchResultsWithoutNulls = batchResults.filter(result => result !== null);
		results.push(...batchResultsWithoutNulls);
	}

	return results;
};

type TriggerDataSourceParams = {
	dataSourceName: string;
	dataSourceSettings: Record<string, any>;
	enableLoop: boolean;
	dataSources: any;
	widgetExternalId: string;
	user: any;
	getDataSource: (widgetExternalId: string, config: any) => Promise<any>;
	handleResponse: (responses: any[]) => void;
	setIsLoading: (loading: boolean) => void;
	errorContext?: string;
};

export const triggerDataSource = async ({
	dataSourceName,
	dataSourceSettings,
	enableLoop,
	dataSources,
	widgetExternalId,
	user,
	getDataSource,
	handleResponse,
	setIsLoading,
	errorContext = 'widget'
}: TriggerDataSourceParams) => {
	if (!dataSourceName || !dataSourceSettings?.[dataSourceName]) return;

	try {
		setIsLoading(true);
		if (enableLoop && dataSources?.order?.orderRow) {
			const allResponses: any = await processOrderRowsInBatches({
				orderRows: dataSources.order.orderRow,
				dataSourceName,
				dataSourceSettings,
				dataSources,
				widgetExternalId,
				user,
				getDataSource,
				batchSize: 5
			});
			if (allResponses.length === 0) {
				return;
			}
			handleResponse(allResponses);
		} else {
			const translatedDataSourceConfig = translateTagsInObject(
				dataSourceSettings[dataSourceName],
				{
					...dataSources,
					providerData: user?.integrations?.[widgetExternalId]?.providerData || {}
				},
				true
			);
			if (!translatedDataSourceConfig) {
				return Promise.resolve(null);
			}

			const response = await getDataSource(widgetExternalId, {
				[dataSourceName]: translatedDataSourceConfig
			});

			handleResponse([response]);
		}
	} catch (err) {
		logError(err, `Failed to trigger ${errorContext} data source`);
	} finally {
		setIsLoading(false);
	}
};
